import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../api-authorization/authorize.service';
import { UserModel } from '../../user.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { tap } from 'rxjs';
import { ResetPasswordDialogComponent } from './reset-password-dialog/reset-password-dialog.component'; // Adjust the path if needed

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.css']
})
export class UserFormComponent implements OnInit {
  userForm: FormGroup;
  isLoading = false;
  passwordVisible = true;
  resetInProgress = false;

  // Auswählbare Gruppen inkl. Kurzbeschreibung der Rechte
  groupOptions = [
    { value: 'SuperAdmin', label: 'SuperAdmin – alles inkl. Rollenvergabe & Ressourcenplanung' },
    { value: 'Admin', label: 'Admin – alles inkl. Benutzerverwaltung' },
    { value: 'Kaufmaennisch', label: 'Kaufmännisch – alles sehen & bearbeiten' },
    { value: 'ProjektleiterKaufmann', label: 'ProjektleiterKaufmann – Stammkunden inkl. Preise/Stundensätze' },
    { value: 'Bauleiter', label: 'Bauleiter – nur zugeordnete Lager/Kunden (bearbeiten)' },
    { value: 'Vorarbeiter', label: 'Vorarbeiter – nur zugeordnete Aufgaben, KEINE Preise' },
    { value: 'Montage', label: 'Montage – nur zugeordnete Lager/Kunden (nur lesen)' },
    { value: 'user', label: 'Benutzer (Standard)' }
  ];

  /** SuperAdmin darf nur ein SuperAdmin vergeben – sonst Option ausblenden. */
  get visibleGroupOptions() {
    if (this.authService.isSuperAdmin()) {
      return this.groupOptions;
    }
    return this.groupOptions.filter(o => o.value !== 'SuperAdmin');
  }

  warehouses: { id: number; name: string }[] = [];
  customers: { id: number; name: string }[] = [];

  constructor(
    public dialogRef: MatDialogRef<UserFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { user: UserModel; isEditMode: boolean },
    private fb: FormBuilder,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.userForm = this.fb.group({
      username: [data.user?.username || '', Validators.required],
      email: [data.user?.email || '', [Validators.required, Validators.email]],
      password: [''],
      roles: [this.getPrimaryRole(data.user?.roles) || 'user', Validators.required],
      warehouseIds: [[]],
      customerIds: [[]]
    });
  }

  ngOnInit() {
    if (this.data.user) {
      this.userForm.patchValue({
        roles: this.getPrimaryRole(this.data.user.roles)
      });
    }

    // Lager- und Kundenliste für die Zuordnung laden.
    this.authService.getWarehouses().subscribe({
      next: warehouses => this.warehouses = warehouses || [],
      error: () => this.warehouses = []
    });
    this.authService.getAssignableCustomers().subscribe({
      next: customers => this.customers = customers || [],
      error: () => this.customers = []
    });

    // Bestehende Zuordnung im Bearbeiten-Modus laden.
    if (this.data.isEditMode && this.data.user?.id) {
      this.authService.getUserAccess(this.data.user.id).subscribe({
        next: access => this.userForm.patchValue({
          warehouseIds: access.warehouseIds || [],
          customerIds: access.customerIds || []
        }),
        error: () => { /* noch keine Zuordnung vorhanden */ }
      });
    }
  }

  /** Zuordnung ist nur für eingeschränkte Gruppen (Bauleiter/Montage/Vorarbeiter) relevant. */
  get isScopedGroup(): boolean {
    const role = this.userForm.get('roles')?.value;
    return role === 'Bauleiter' || role === 'Montage' || role === 'Vorarbeiter';
  }

  private getPrimaryRole(roles: string | string[] | undefined): string {
    if (Array.isArray(roles)) {
      return roles[0] || 'user';
    }
    return roles || 'user';
  }

  saveUser() {
    this.isLoading = true;

    const updatedUser: UserModel = {
      id: this.data.user.id || '',
      username: this.userForm.value.username,
      email: this.userForm.value.email,
      password: this.userForm.value.password,
      roles: this.userForm.value.roles,
      role: this.userForm.value.roles
    };

    const request$ = this.data.isEditMode
      ? this.authService.updateUser(updatedUser)
      : this.authService.register(updatedUser);

    request$.pipe(
      tap(
        () => {
          // Im Bearbeiten-Modus zusätzlich die Lager-/Kunden-Zuordnung speichern.
          if (this.data.isEditMode && this.data.user.id) {
            const warehouseIds = this.userForm.value.warehouseIds || [];
            const customerIds = this.userForm.value.customerIds || [];
            this.authService.setUserAccess(this.data.user.id, warehouseIds, customerIds).subscribe({
              next: () => this.finishSave(updatedUser),
              error: () => {
                this.snackBar.open('Benutzer gespeichert, aber Zuordnung fehlgeschlagen.', 'Schließen', { duration: 5000 });
                this.finishSave(updatedUser);
              }
            });
          } else {
            this.finishSave(updatedUser);
          }
        },
        (error: HttpErrorResponse) => this.handleError(error)
      )
    ).subscribe();
  }

  private finishSave(updatedUser: UserModel) {
    this.isLoading = false;
    this.dialogRef.close(updatedUser);
    this.snackBar.open(this.data.isEditMode ? 'Benutzer aktualisiert' : 'Benutzer erstellt', 'Schließen', { duration: 3000 });
  }

  private handleError(error: HttpErrorResponse) {
    console.error(error);
    this.snackBar.open(`Fehler: ${error.error}`, 'Schließen', { duration: 5000 });
    this.isLoading = false;
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  openResetPasswordDialog() {
    const dialogRef = this.dialog.open(ResetPasswordDialogComponent, {
      width: '420px',
      data: { user: this.data.user }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        const userId = this.data.user.id || '';
        if (!userId) {
          this.snackBar.open('Benutzer-ID nicht gefunden.', 'Schließen', { duration: 4000 });
          return;
        }

        this.resetInProgress = true;
        this.authService.resetPassword(userId).subscribe({
          next: response => {
            this.resetInProgress = false;
            const message = response?.message || 'Ein Einmalpasswort wurde versendet.';
            this.snackBar.open(message, 'Schließen', { duration: 4000 });
          },
          error: (error: HttpErrorResponse) => {
            this.resetInProgress = false;
            console.error('Error resetting password', error);
            const errorMessage = typeof error.error === 'string' ? error.error : 'Fehler beim Zurücksetzen des Passworts.';
            this.snackBar.open(errorMessage, 'Schließen', { duration: 5000 });
          }
        });
      }
    });
  }
}
